/**
 * Styles existing Judge.me preview badges in place (no new badge nodes).
 * - PDP: format once on init
 * - Product / regimen cards: format when the card enters the viewport, then unobserve
 * GraphQL-built cards sanitize badge HTML in the request (skip this path).
 */
(function () {
  const DONE_ATTR = 'data-er-rating-done';
  const CARD_SELECTOR = '.product-card, .regimen-card';

  /** @type {IntersectionObserver | null} */
  let cardObserver = null;

  /**
   * @param {string | null | undefined} value
   * @returns {string | null}
   */
  function formatRating(value) {
    const rating = parseFloat(value);
    if (!Number.isFinite(rating) || rating <= 0) return null;
    // Truncate to 1 decimal (5.77 → 5.7), do not round up
    return (Math.floor(rating * 10) / 10).toFixed(1);
  }

  /**
   * Stops Judge.me from hydrating a Liquid card badge and appending review count.
   * @param {Element} badge
   */
  function detachJudgemeWidget(badge) {
    const widget = badge.closest('.jdgm-preview-badge');
    if (!(widget instanceof HTMLElement)) return;

    widget.classList.remove('jdgm-widget');
    widget.removeAttribute('data-id');
    widget.setAttribute('data-auto-install', 'false');
  }

  /**
   * @param {Element} badge
   */
  function formatBadge(badge) {
    if (!(badge instanceof HTMLElement)) return;
    if (badge.hasAttribute(DONE_ATTR)) return;

    const rating = formatRating(badge.getAttribute('data-average-rating'));
    const reviews = parseInt(badge.getAttribute('data-number-of-reviews') || '0', 10);
    const isProductCard = Boolean(badge.closest(CARD_SELECTOR));

    if (!rating) {
      badge.setAttribute(DONE_ATTR, '');
      badge.classList.add('er-rating-empty');
      return;
    }

    const stars = badge.querySelectorAll('.jdgm-star');
    stars.forEach((star, index) => {
      if (index === 0) {
        star.classList.add('jdgm--on', 'er-rating-star-on');
        star.classList.remove('jdgm--off', 'jdgm--half');
      } else {
        star.classList.add('er-rating-star-off');
      }
    });

    const starsEl = badge.querySelector('.jdgm-prev-badge__stars');
    if (starsEl) {
      starsEl.setAttribute('data-score', rating);
      starsEl.setAttribute('aria-label', `${rating} stars`);
    }

    const textEl = badge.querySelector('.jdgm-prev-badge__text');
    if (textEl) {
      if (isProductCard) {
        detachJudgemeWidget(badge);
        // Sanitize "4.7 102" → "4.7"
        textEl.textContent = rating;
      } else {
        textEl.textContent = reviews > 0 ? `${reviews} Ratings` : '';
      }
    }

    badge.classList.add('er-rating');
    badge.classList.toggle('er-rating--card', isProductCard);
    badge.classList.toggle('er-rating--pdp', !isProductCard);
    badge.setAttribute(DONE_ATTR, '');
  }

  /**
   * Formats non-card (e.g. PDP) badges immediately.
   * @param {ParentNode} [root]
   */
  function formatPdpBadges(root = document) {
    root.querySelectorAll('.jdgm-prev-badge').forEach((badge) => {
      if (badge.closest(CARD_SELECTOR)) return;
      formatBadge(badge);
    });
  }

  /**
   * Shared IntersectionObserver for product/regimen cards.
   * INP risk: low — formats one badge and unobserves; no layout reads.
   * @returns {IntersectionObserver}
   */
  function getCardObserver() {
    if (cardObserver) return cardObserver;

    cardObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const card = entry.target;
          cardObserver?.unobserve(card);

          const badge = card.querySelector(`.jdgm-prev-badge:not([${DONE_ATTR}])`);
          if (badge) formatBadge(badge);

          card.setAttribute(DONE_ATTR, '');
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0 }
    );

    return cardObserver;
  }

  /**
   * Observe product/regimen cards in scope until each is formatted once.
   * @param {ParentNode | Element | Document} [root]
   */
  function observeCards(root = document) {
    const scope = root instanceof Element || root instanceof Document ? root : document;
    const observer = getCardObserver();

    scope.querySelectorAll(`${CARD_SELECTOR}:not([${DONE_ATTR}])`).forEach((card) => {
      const badge = card.querySelector('.jdgm-prev-badge');

      if (!badge || badge.hasAttribute(DONE_ATTR) || badge.classList.contains('er-rating')) {
        card.setAttribute(DONE_ATTR, '');
        return;
      }

      observer.observe(card);
    });
  }

  /**
   * Immediate format for a scope (PDP + any cards). Prefer observeCards for grids.
   * @param {ParentNode | Element | Document | null | undefined} root
   */
  function formatAll(root) {
    const scope = root instanceof Element || root instanceof Document ? root : document;
    scope.querySelectorAll(`.jdgm-prev-badge:not([${DONE_ATTR}])`).forEach(formatBadge);
    scope.querySelectorAll(`${CARD_SELECTOR}:not([${DONE_ATTR}])`).forEach((card) => {
      card.setAttribute(DONE_ATTR, '');
    });
  }

  function init() {
    formatPdpBadges(document);
    observeCards(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.formatJudgemeRatingBadges = formatAll;
  window.observeJudgemeRatingCards = observeCards;
})();
